import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_TRUTH_SOCIAL_NOTIFICATION_SETTINGS,
  TruthSocialNotificationSettings,
} from "@/shared/api/firehose/truthSocialNotificationRules";

const STORAGE_KEY = "truth.social.notification.settings";

/**
 * Merge stored (possibly partial / older-shape) settings over defaults so newly
 * added fields stay safe and bad data falls back gracefully.
 */
function parseSettings(raw: string | null): TruthSocialNotificationSettings {
  if (!raw) return DEFAULT_TRUTH_SOCIAL_NOTIFICATION_SETTINGS;
  try {
    const parsed = JSON.parse(raw) as Partial<TruthSocialNotificationSettings>;
    return {
      ...DEFAULT_TRUTH_SOCIAL_NOTIFICATION_SETTINGS,
      ...parsed,
      keywords: Array.isArray(parsed.keywords)
        ? parsed.keywords
        : DEFAULT_TRUTH_SOCIAL_NOTIFICATION_SETTINGS.keywords,
    };
  } catch {
    return DEFAULT_TRUTH_SOCIAL_NOTIFICATION_SETTINGS;
  }
}

/**
 * Manages the Truth Social sound-notification filters (burst throttle, keyword
 * allowlist, content-type skips) via localStorage. Mirrors `useWatchlistAlertMode`:
 * lazy init, write-back on change, cross-tab sync via the `storage` event.
 */
export function useTruthSocialNotificationSettings() {
  const [settings, setSettingsState] = useState<TruthSocialNotificationSettings>(
    () => {
      try {
        return parseSettings(localStorage.getItem(STORAGE_KEY));
      } catch {
        return DEFAULT_TRUTH_SOCIAL_NOTIFICATION_SETTINGS;
      }
    }
  );

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // localStorage not available
    }
  }, [settings]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setSettingsState(parseSettings(e.newValue));
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const updateSettings = useCallback(
    (patch: Partial<TruthSocialNotificationSettings>) => {
      setSettingsState((prev) => ({ ...prev, ...patch }));
    },
    []
  );

  return { settings, updateSettings };
}
